import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';

const ADMIN_EMAILS = ['admin@dayclap.com'];

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (!error && data) {
        const customersOnly = data.filter(u =>
          u.role !== 'admin' && !ADMIN_EMAILS.includes(u.email?.toLowerCase())
        );
        setUsers(customersOnly);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleBlock = async (userId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_blocked: !currentStatus, updated_at: new Date().toISOString() })
        .eq('id', userId);
      if (!error) {
        setUsers(users.map(u => u.id === userId ? { ...u, is_blocked: !currentStatus } : u));
      }
    } catch (err) {
      console.error('Error toggling block:', err);
    }
  };

  const handleDeleteUser = async (userId) => {
    setDeleting(true);
    try {
      const { data, error } = await supabase.rpc('admin_delete_user', { p_user_id: userId });
      if (error) {
        console.error('Delete error:', error);
        alert('Failed to delete user: ' + error.message);
      } else if (data?.success) {
        setUsers(users.filter(u => u.id !== userId));
        setSelectedUser(null);
      } else {
        alert('Failed to delete user: ' + (data?.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Delete exception:', err);
      alert('Failed to delete user: ' + err.message);
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchSearch = u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' ? true :
      filter === 'active' ? !u.is_blocked :
      filter === 'blocked' ? u.is_blocked : true;
    return matchSearch && matchFilter;
  });

  const activeCount = users.filter(u => !u.is_blocked).length;
  const blockedCount = users.filter(u => u.is_blocked).length;

  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Customer Management</h2>
          <p className="text-gray-500 mt-1">{users.length} registered customers</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2">
            <SafeIcon name="Users" className="text-gray-400" />
            <span className="text-sm font-medium text-gray-600">{filteredUsers.length} showing</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { icon: 'Users', color: 'blue', value: users.length, label: 'Total Customers' },
          { icon: 'CheckCircle', color: 'green', value: activeCount, label: 'Active' },
          { icon: 'XCircle', color: 'red', value: blockedCount, label: 'Blocked' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 bg-${stat.color}-100 rounded-lg flex items-center justify-center`}>
                <SafeIcon name={stat.icon} className={`text-${stat.color}-600`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[280px]">
          <SafeIcon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none text-sm" />
        </div>
        <div className="flex gap-2">
          {['all', 'active', 'blocked'].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-5 py-3 rounded-xl text-sm font-medium transition-all ${
                filter === f
                  ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'all' && <span className="ml-1.5 text-xs opacity-75">({users.length})</span>}
              {f === 'active' && <span className="ml-1.5 text-xs opacity-75">({activeCount})</span>}
              {f === 'blocked' && <span className="ml-1.5 text-xs opacity-75">({blockedCount})</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="text-left px-8 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
              <th className="text-left px-8 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
              <th className="text-left px-8 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Joined</th>
              <th className="text-right px-8 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-8 py-16 text-center">
                  <SafeIcon name="Users" className="text-4xl text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-400 font-medium">No customers found</p>
                  <p className="text-gray-300 text-sm mt-1">Try adjusting your search or filters</p>
                </td>
              </tr>
            ) : (
              filteredUsers.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-4">
                      <img src={u.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${u.email}`}
                        alt={u.name} className="w-11 h-11 rounded-full ring-2 ring-gray-100" />
                      <div>
                        <p className="font-semibold text-gray-800">{u.name || 'Unnamed'}</p>
                        <p className="text-xs text-gray-400">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${
                      u.is_blocked ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${u.is_blocked ? 'bg-red-500' : 'bg-green-500'}`} />
                      {u.is_blocked ? 'Blocked' : 'Active'}
                    </span>
                  </td>
                  <td className="px-8 py-5 text-sm text-gray-500">{formatDate(u.created_at)}</td>
                  <td className="px-8 py-5 text-right">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setSelectedUser(u)}
                        className="text-xs text-blue-600 hover:text-blue-800 font-semibold bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg transition-colors">View</button>
                      <button onClick={() => toggleBlock(u.id, u.is_blocked)}
                        className={`text-xs font-semibold px-4 py-2 rounded-lg transition-colors ${
                          u.is_blocked ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-red-600 bg-red-50 hover:bg-red-100'
                        }`}>{u.is_blocked ? 'Unblock' : 'Block'}</button>
                      <button onClick={() => setDeleteConfirm(u)}
                        className="text-xs text-red-600 hover:text-red-800 font-semibold bg-red-50 hover:bg-red-100 px-4 py-2 rounded-lg transition-colors">
                        <SafeIcon name="Trash2" className="inline w-3 h-3 mr-1" />Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl">
            <div className="p-8 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-800">Customer Details</h3>
              <button onClick={() => setSelectedUser(null)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                <SafeIcon name="X" />
              </button>
            </div>
            <div className="p-8">
              <div className="text-center mb-8">
                <img src={selectedUser.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedUser.email}`}
                  alt={selectedUser.name} className="w-24 h-24 rounded-full mx-auto mb-4 ring-4 ring-pink-100" />
                <h4 className="text-xl font-bold text-gray-800">{selectedUser.name || 'Unnamed'}</h4>
                <p className="text-sm text-gray-500 mt-1">{selectedUser.email}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Status', value: selectedUser.is_blocked ? 'Blocked' : 'Active', color: selectedUser.is_blocked ? 'text-red-500' : 'text-green-500' },
                  { label: 'Location', value: selectedUser.location || 'Not set' },
                  { label: 'Joined', value: formatDate(selectedUser.created_at) },
                  { label: 'Bio', value: selectedUser.bio || 'Not set' },
                ].map((item) => (
                  <div key={item.label} className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                    <p className={`text-sm font-semibold ${item.color || 'text-gray-800'}`}>{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-8 pt-0 flex gap-3">
              <button onClick={() => setDeleteConfirm(selectedUser)}
                className="flex-1 text-red-600 bg-red-50 hover:bg-red-100 font-semibold py-3 rounded-xl transition-colors text-sm flex items-center justify-center gap-2">
                <SafeIcon name="Trash2" className="w-4 h-4" /> Delete Customer
              </button>
              <button onClick={() => toggleBlock(selectedUser.id, selectedUser.is_blocked)}
                className={`flex-1 font-semibold py-3 rounded-xl transition-colors text-sm ${
                  selectedUser.is_blocked ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-red-600 bg-red-50 hover:bg-red-100'
                }`}>{selectedUser.is_blocked ? 'Unblock' : 'Block'}</button>
              <button onClick={() => setSelectedUser(null)}
                className="flex-1 bg-gray-100 text-gray-700 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors text-sm">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon name="AlertTriangle" className="text-red-500 text-3xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Delete Customer?</h3>
              <p className="text-gray-500 text-sm mb-1">
                You are about to permanently delete:
              </p>
              <p className="font-bold text-gray-800 mb-4">{deleteConfirm.name || 'Unnamed'} ({deleteConfirm.email})</p>
              <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-6 text-left">
                <p className="text-red-700 text-sm font-semibold mb-2">This will permanently remove:</p>
                <ul className="text-red-600 text-xs space-y-1">
                  <li>• Their profile and all personal data</li>
                  <li>• All invitations they sent or received</li>
                  <li>• All connections and notifications</li>
                </ul>
                <p className="text-red-500 text-xs mt-3 font-medium">⚠️ This action cannot be undone.</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleting}
                  className="flex-1 bg-gray-100 text-gray-700 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteUser(deleteConfirm.id)}
                  disabled={deleting}
                  className="flex-1 bg-red-500 text-white font-semibold py-3 rounded-xl hover:bg-red-600 transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Deleting...</>
                  ) : (
                    <><SafeIcon name="Trash2" className="w-4 h-4" /> Delete Permanently</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;